import { Link } from 'react-router-dom';
import { DocumentTextIcon } from '@heroicons/react/24/outline';
import { Badge, Card, getStatusBadgeVariant } from '@/components/common';
import { usePolicies } from '@/hooks/usePolicies';
import { lifecycleBadgeLabel, type Policy } from '@/types';
import { titleCase } from '@/utils';

/**
 * M-01 — "My Policies" on the customer's Settings > Profile: each policy with
 * the broker and the agent it is mapped to.
 *
 * The list is the ordinary `/policies/` read, which the server already narrows
 * to the customer's own policies (`customer__user=user`). Broker and agent come
 * from each row's `advisors` -- per POLICY, because a customer can hold
 * policies at two brokers or sold by two agents -- falling back to the plain
 * `broker_name` / `posp_name` columns every role gets.
 */

function PolicyNumber({ policy }: { policy: Policy }) {
  const theirs = policy.insurer_policy_number?.trim();
  if (theirs) {
    return <span className="font-medium text-gray-900 dark:text-white">{theirs}</span>;
  }
  // No insurer number recorded yet: our reference is all there is, and it is
  // labelled as such so it is not mistaken for the number on their document.
  return (
    <span className="font-medium text-gray-900 dark:text-white">
      {policy.policy_number}
      <span className="ml-1 text-xs font-normal text-gray-500 dark:text-gray-400">(internal ref)</span>
    </span>
  );
}

function statusLabel(policy: Policy): string {
  const lifecycle = lifecycleBadgeLabel(policy);
  return lifecycle !== '—' ? lifecycle : titleCase(policy.status);
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
      <div className="text-sm text-gray-900 dark:text-white truncate">{children}</div>
    </div>
  );
}

export function MyPoliciesCard() {
  const { data, isLoading, isError, refetch } = usePolicies(1);
  const policies = data?.results ?? [];

  return (
    <Card>
      <div className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center">
            <DocumentTextIcon className="w-6 h-6 text-amber-600 dark:text-amber-400" />
          </div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">My Policies</h2>
        </div>

        {isLoading ? (
          <div className="animate-pulse space-y-3" aria-label="Loading your policies">
            {[0, 1].map((i) => (
              <div key={i} className="h-16 rounded-lg bg-gray-100 dark:bg-gray-700" />
            ))}
          </div>
        ) : isError ? (
          <div className="py-4 text-sm">
            <p className="text-gray-500 dark:text-gray-400">Failed to load your policies.</p>
            <button
              type="button"
              onClick={() => refetch()}
              className="mt-2 font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
            >
              Try again
            </button>
          </div>
        ) : policies.length === 0 ? (
          <p className="py-4 text-sm text-gray-500 dark:text-gray-400">You have no policies with us yet.</p>
        ) : (
          <>
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {policies.map((policy) => {
                const brokerName = policy.advisors?.broker.name || policy.broker_name;
                const agentName = policy.advisors ? policy.advisors.agent?.name : policy.posp_name;
                return (
                  <li key={policy.id} data-testid="my-policy-row" className="py-3">
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
                      <Field label="Policy number">
                        <Link to={`/policies/${policy.id}`} className="hover:underline">
                          <PolicyNumber policy={policy} />
                        </Link>
                      </Field>
                      <Field label="Insurer">{policy.insurer_name || '—'}</Field>
                      <Field label="Status">
                        <Badge variant={getStatusBadgeVariant(policy.lifecycle_status ?? policy.status)} size="sm">
                          {statusLabel(policy)}
                        </Badge>
                      </Field>
                      <Field label="Broker">{brokerName || '—'}</Field>
                      <Field label="Agent / POSP">
                        {agentName || <span className="italic text-gray-400 dark:text-gray-500">Not assigned</span>}
                      </Field>
                    </div>
                  </li>
                );
              })}
            </ul>
            {data?.next && (
              <Link
                to="/policies"
                className="mt-3 inline-block text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
              >
                View all {data.count} policies
              </Link>
            )}
          </>
        )}
      </div>
    </Card>
  );
}
